import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { first, map, switchMap } from 'rxjs/operators';
import {
  Category,
  Event,
  EventCreateOptions,
  Organizer,
  Platforms,
  TicketingPlatform,
  defaultCategory,
  FestikEvent,
  OrganizerAccessRight,
  EventStatus,
  PayoutPolicy,
  CategoriesGroup,
  Currencies,
  AutomationsStatus,
  defaultCategoriesGroup,
  FestikSpectacle,
  TicketTemplate,
} from '../../../models';
import { PartnerUtils } from './utils';
import { Utils } from '../../../shared/utils';
import { PdfUtils } from '../../../shared/utils/pdf-utils';
import { createTicketTemplate } from '../../../shared-dashboard/pdfmake';
import { from } from 'rxjs';
import { DatePipe } from '@angular/common';
import { UploaderService } from '../../../services';

@Injectable({
  providedIn: 'root',
})
export class FestikEventService {
  private http = inject(HttpClient);
  private uploaderService = inject(UploaderService);

  configUrl = environment.apiUrl;

  getFestikEvents(organizerId: number) {
    const route = `${this.configUrl}w/a/festikEvents/organizations/${organizerId}/events`;

    return this.http.get<{
      festikEvents: FestikEvent,
      reelaxEvents: Event[],
      organizer: Organizer,
    }>(route).pipe(
      first(),
    );
  }

  loadEvents(options: EventCreateOptions) {
    return this.getFestikEvents(options.organizer.id).pipe(
      map( (events) => this.mapFestikToReelaxEvent(
        events.festikEvents,
        events.reelaxEvents,
        events.organizer,
        options.splitRepresentations,
        options.eventTemplate,
        options.priceScale,
        options.defaultStatus,
      )),
    );
  }

  public mapFestikToReelaxEvent(
    festik: FestikEvent,
    reelaxEvents: Event[],
    organizer: Organizer,
    splitRepresentations: boolean,
    eventTemplate: Event,
    priceScale: number,
    defaultStatus: EventStatus,
  ) {
    const eventsToCreate = [];
    if (splitRepresentations) {
      for (const spectacleInfo of festik.spectacles) {
        const newEventDateStart = spectacleInfo.representation.timestamp ?
          new Date(spectacleInfo.representation.timestamp * 1000) : undefined;
        const newEventDateEnd = undefined;
        eventsToCreate.push({
          ...eventTemplate,
          id: reelaxEvents.find(
            (r) => r.TicketingPlatforms.some((t) => `${spectacleInfo.representation.id}` === t.platformEventId),
          )?.id,
          name: `${festik.billetterie.name?.trim()} • ${spectacleInfo.spectacle.name?.trim()}`,
          organizerName: eventTemplate?.organizerName,
          location: ' ', // TODO ask festik to send this information
          dateStart: newEventDateStart,
          dateEnd: newEventDateEnd,
          resaleEnd: PartnerUtils.computeDateRelativeTo(
            eventTemplate?.resaleEnd,
            newEventDateStart,
            eventTemplate?.dateStart,
          ),
          imgUrl: undefined, // TODO ask festik to send this information
          color: undefined, // TODO ask festik to send this information
          address: [ // TODO ask festik to send this information
          // spectacleInfo.representation.address.address1,
          // spectacleInfo.representation.address.address2,
          // spectacleInfo.representation.address.city,
          // spectacleInfo.representation.address.zip_code,
          // spectacleInfo.representation.address.state,
          // spectacleInfo.representation.address.country,
          ].filter((x) => !!x).join(', '),
          isPartner: true,
          allowAPIImageUpdate: true,
          allowPartnerUpdate: true,
          allowPayinRefund: true,
          status: defaultStatus ? defaultStatus : EventStatus.Online,
          reelaxEvent: reelaxEvents.find(
            (r) => r.TicketingPlatforms.some((t) => `${spectacleInfo.representation.id}` === t.platformEventId),
          ),
          ticketshop: spectacleInfo.spectacle.url,
          TicketingPlatforms: [new TicketingPlatform({
            platform: Platforms.Festik,
            platformEventId: `${spectacleInfo.representation.id}`,
            payoutPolicy: PayoutPolicy.Auto,
          })],
          Categories: spectacleInfo.tarifs.map((t, j) =>
            new Category({
              ...defaultCategory,
              ...(eventTemplate?.Categories?.[0]),
              available: t.prix > 0,
              currency: Currencies.Euro,
              expiryDate: spectacleInfo.representation.timestamp ?
                new Date(spectacleInfo.representation.timestamp) : undefined,
              description: undefined,
              minimalTotalFees: 300,
              minimalSalePrice: priceScale ? Math.round(t.prix * priceScale * 100) : Math.round(t.prix * 50),
              maximalSalePrice: Math.round(t.prix * 100),
              name: t.name,
              price: Math.round(t.prix * 100),
              platformsForeignIds: {[Platforms.Festik]: `${t.id}+${spectacleInfo.representation.id}`},
              order: j,
            }),
          ),
          // TODO ask festik to send this information
          PostalAddress: {
            address1: '',
            address2: '',
            city: '',
            zip_code: '',
            country: '',
          },
          Organizers: eventTemplate?.Organizers?.length >= 1 ? [
            ...eventTemplate.Organizers,
            new Organizer({
              ...organizer,
              EventOrganizer: {
                accessRight: OrganizerAccessRight.Owner,
              },
            }),
          ] : [new Organizer({
            ...organizer,
            EventOrganizer: {
              accessRight: OrganizerAccessRight.Owner,
            },
          })],
          Automations: eventTemplate?.Automations?.map( (automation) => {
            const oldAutoTime = new Date(automation.time);
            const templateDateStart = automation?.eventDateStart ?
              new Date(automation?.eventDateStart) : new Date(eventTemplate?.dateStart);
            const newTime = new Date(
              newEventDateStart.getTime() - (templateDateStart.getTime() - oldAutoTime.getTime()),
            ).toString();
            return {
              status: AutomationsStatus.Created,
              task: automation.task,
              time: newTime,
              type: automation.type,
            };
          }),
          WaitListConfig: eventTemplate?.WaitListConfig ?? undefined,
          EventPersonalization: eventTemplate?.EventPersonalization ?? undefined,
        });
      }
    } else {
      const categoriesGroupNames = new Set([...festik.spectacles.map( (s) => s.spectacle.name?.trim())]);
      const newEventDateStart = festik.spectacles.reduce((acc, val) => {
        if (!acc || (val.representation.timestamp && val.representation.timestamp * 1000 < acc.getTime())) {
          return new Date(val.representation.timestamp * 1000);
        }
        return acc;
      }, festik.spectacles[0]?.representation.timestamp ?
        new Date(festik.spectacles[0]?.representation.timestamp * 1000) : new Date(),
      );
      const newEventDateEnd = festik.spectacles.reduce((acc, val) => {
        if (!acc || (val.representation.timestamp && val.representation.timestamp * 1000 > acc.getTime())) {
          return new Date(val.representation.timestamp * 1000);
        }
        return acc;
      }, festik.spectacles[0]?.representation.timestamp ?
        new Date(festik.spectacles[0]?.representation.timestamp * 1000) : undefined,
      );
      eventsToCreate.push({
        ...eventTemplate,
        id: reelaxEvents.find(
          (r) => r.TicketingPlatforms.some((t) =>
            festik.spectacles.some((s) => `${s.representation.id}` === t.platformEventId),
          ),
        )?.id,
        name: festik.billetterie.name?.trim(),
        organizerName: eventTemplate?.organizerName,
        location: ' ', // TODO ask festik to send this information
        dateStart: newEventDateStart,
        dateEnd: newEventDateEnd,
        resaleEnd: PartnerUtils.computeDateRelativeTo(
          eventTemplate?.resaleEnd,
          newEventDateStart,
          eventTemplate?.dateStart,
        ),
        imgUrl: undefined, // TODO ask festik to send this information
        color: undefined, // TODO ask festik to send this information
        address: [ // TODO ask festik to send this information
        // festik.address.address1,
        // festik.address.address2,
        // festik.address.city,
        // festik.address.zip_code,
        // festik.address.state,
        // festik.address.country,
        ].filter((x) => !!x).join(', '),
        isPartner: true,
        allowAPIImageUpdate: true,
        allowPartnerUpdate: true,
        allowPayinRefund: true,
        status: defaultStatus ? defaultStatus : EventStatus.Online,
        reelaxEvent: reelaxEvents.find(
          (r) => r.TicketingPlatforms.some((t) =>
            festik.spectacles.some((s) => `${s.representation.id}` === t.platformEventId),
          ),
        ),
        ticketshop: festik.billetterie.url,
        TicketingPlatforms: festik.spectacles.map((s) =>
          new TicketingPlatform({
            platform: Platforms.Festik,
            platformEventId: `${s.representation.id}`,
            payoutPolicy: PayoutPolicy.Auto,
          }),
        ),
        Categories: festik.spectacles.flatMap( (s: FestikSpectacle, i) =>
          s.tarifs.map((t, j) =>
            new Category({
              ...defaultCategory,
              ...(eventTemplate?.Categories?.[0]),
              available: t.prix > 0,
              categoriesGroupId: s.spectacle.name?.trim() as any,
              currency: Currencies.Euro,
              expiryDate: s.representation.timestamp ? new Date(s.representation.timestamp) : undefined,
              description: undefined,
              minimalTotalFees: 300,
              minimalSalePrice: priceScale ? Math.round(t.prix * priceScale * 100) : Math.round(t.prix * 50),
              maximalSalePrice: Math.round(t.prix * 100),
              name: t.name?.trim(),
              price: Math.round(t.prix * 100),
              platformsForeignIds: {[Platforms.Festik]: `${t.id}+${s.representation.id}`},
              order: j,
            }),
          ),
        ),
        CategoriesGroups: [...categoriesGroupNames].map( (group, i) => new CategoriesGroup({
          ...defaultCategoriesGroup,
          name: group,
          order: i,
          platformsForeignIds: {[Platforms.Festik]: group},
        })),
        // TODO ask festik to send this information
        PostalAddress: {
          address1: '',
          address2: '',
          city: '',
          zip_code: '',
          country: '',
        },
        Organizers: eventTemplate?.Organizers?.length >= 1 ? [
          ...eventTemplate.Organizers,
          new Organizer({
            ...organizer,
            EventOrganizer: {
              accessRight: OrganizerAccessRight.Owner,
            },
          }),
        ] : [new Organizer({
          ...organizer,
          EventOrganizer: {
            accessRight: OrganizerAccessRight.Owner,
          },
        })],
        Automations: eventTemplate?.Automations?.map( (automation) => {
          const oldAutoTime = new Date(automation.time);
          const templateDateStart = automation?.eventDateStart ?
            new Date(automation?.eventDateStart) : new Date(eventTemplate?.dateStart);
          const newTime = new Date(
            newEventDateStart.getTime() - (templateDateStart.getTime() - oldAutoTime.getTime()),
          ).toString();
          return {
            status: AutomationsStatus.Created,
            task: automation.task,
            time: newTime,
            type: automation.type,
          };
        }),
        WaitListConfig: eventTemplate?.WaitListConfig ?? undefined,
        EventPersonalization: eventTemplate?.EventPersonalization ?? undefined,
      });
    }
    return eventsToCreate;
  }


  public createEventTicketTemplate(event: Event, document: Document, options: EventCreateOptions) {
    const eventUrl = 'fes-' + Utils.stringToURLallowed(event.name);
    const textLines: string[] = [
      event.organizerName ?? event.Organizers[0].name,
      event.name.trim(),
      (event.dateEnd ? 'du ' : '') + new DatePipe('fr-FR').transform(
        event.dateStart,
        'EEEE d MMMM y HH:mm',
      ),
      event.dateEnd ? ('au ' + new DatePipe('fr-FR').transform(
        event.dateEnd,
        'EEEE d MMMM y HH:mm',
      )) : '',
      event.EventPersonalization?.ticketTemplateInformation,
      /*
      // address not yet available on Festik creation.
      // On rodrigue template, it is inserted between event date and personalization
      event.PostalAddress.name,
      [
        event.PostalAddress.address1,
        event.PostalAddress.address2,
        event.PostalAddress.zip_code,
        event.PostalAddress.city,
      ].filter( (a) => !!a).join(', '),
      */
    ];

    const reelaxTicketMask = {
      x: 75, // old template was 44
      y: 78, // old template was 43
      h: 156, // little less than manual because of zoom offset
      w: 200,
    };

    return from(createTicketTemplate([
      {
        alignment: 'center',
        fontSize: 12,
        margin: [0, 0, 0, 0],
        text: textLines[0],
      },
      {
        alignment: 'center',
        fontSize: 12,
        margin: [0, 5, 0, 0],
        text: textLines[1],
        bold: true,
      },
      {
        alignment: 'center',
        fontSize: 12,
        margin: [0, 5, 0, 0],
        text: textLines[2],
      },
      {
        alignment: 'center',
        fontSize: 12,
        margin: [0, 5, 0, 0],
        text: textLines[3],
      },
      {
        alignment: 'center',
        fontSize: 12,
        margin: [0, 5, 0, 0],
        text: textLines[4],
      },
    ], reelaxTicketMask).then( (pdfBlob) => PdfUtils.pdfToImageDataURLAsync(Utils.blobToFile(pdfBlob, eventUrl + '.jpeg'), document))).pipe(
      switchMap( (convertedPdf) => {
        const image = Utils.blobToFile(Utils.base64ToBlob(convertedPdf[0].base64), eventUrl + '.jpeg');

        return this.uploaderService.postTicketTemplate(image, eventUrl);
      }),
      map( (templateImgUrl) => {
        const template = new TicketTemplate({
          // first default values
          organizerLegalName: event.organizerName,
          bigSeatingInfo: true,
          isLandscape: false,
          bigCode128: false,
          bigCategory: true,
          printerVersion: 4,
          name: 'default',
          // 2nd use template informations
          ...options.eventTemplate?.TicketTemplates?.[0],
          // 3rd set own values
          modifiedBy: undefined,
          templateUrl: templateImgUrl.downloadUrl,
          codeXCoordinate: reelaxTicketMask.x,
          codeYCoordinate: reelaxTicketMask.y,
        });
        return template;
      }),
    );

  }

}
